export const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export const formatDate = (dateStr: string | Date) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const normalizeIgnoreValue = (value: string | null | undefined) =>
  (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Whether a pattern is long enough for the backend to match with. Shorter ones are stored and
 * listed but silently never fire, so the UI blocks them rather than saving a dead rule.
 * Same floor as the backend's MIN_IGNORE_PATTERN_LENGTH.
 */
export const isUsableIgnorePattern = (pattern: string): boolean =>
  normalizeIgnoreValue(pattern).replace(/[^a-z0-9]/g, '').length >= 3;

/**
 * Whether an ignore-list pattern would catch a deposit, for the preview shown before adding one.
 * Advisory only — the backend's matchIgnoredDepositPayer is authoritative. Keep the two in step.
 */
export const matchesIgnorePattern = (
  deposit: {
    name?: string | null;
    merchantName?: string | null;
    paymentMetaPayer?: string | null;
    counterparties?: string[] | null;
    payerNameOnDeposit?: string | null;
  },
  pattern: string
): boolean => {
  if (!isUsableIgnorePattern(pattern)) {
    return false;
  }

  const needle = normalizeIgnoreValue(pattern);

  return [
    deposit.name,
    deposit.merchantName,
    deposit.paymentMetaPayer,
    deposit.payerNameOnDeposit,
    ...(deposit.counterparties ?? []),
  ]
    .map(normalizeIgnoreValue)
    .some((haystack) => haystack.length > 0 && haystack.includes(needle));
};

export const formatDateTime = (dateStr: string | Date) =>
  new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
